import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BasicModal from '__components/Modals/presets/BasicModal';
import { useHistory } from 'react-router-dom';
import {
  SMART_CONNECTOR_TYPES,
  SMARTCONNECTORS_PATHNAME,
} from 'pages/SmartConnectors/constants';
import Select from '__components/Inputs/Select';
import type {
  GenericOption,
  SmartConnector,
  SmartConnectorType,
} from 'pages/SmartConnectors/types';
import TextInput from '__components/Inputs/TextInput';
import SmartConnectorService from 'services/SmartConnectorService';
import CustomObjectsService from 'services/CustomObjectsService';
import { getOriginalError } from 'services/AxiosService';
import { useToast, toastVariant } from '__components/ToastProvider';
import { getErrorMessage } from 'hooks/useErrors';
import { useFlashTransition } from 'hooks/useFlashState';
import { useAsync } from 'react-use';
import { getDataQAForInput } from 'components/Inputs/helpers';
import { getSmartConnectorTypeOptions } from '../SmartConnector/helpers';
import { useSelectTypeaheadWithScroll } from '__components/Inputs/Select/hooks';
import { Entities } from 'components/Inputs/Select/hooks';

const errorsOrder: Partial<Record<keyof SmartConnector, number>> = {
  name: 0,
  custom_object: 1,
  connector_type: 2,
};

const useErrors = () => {
  const [errors, setErrors] = useState<
    Partial<Record<keyof SmartConnector, string>>
  >({
    name: '',
    custom_object: '',
    connector_type: '',
    activity_object: '',
  });

  const [message, showMessage, flashErrorMessage, cancelFlash] =
    useFlashTransition();

  const [firstErrorKey] = (
    Object.entries(errors) as [keyof SmartConnector, string][]
  )
    .filter(([, v]) => v)
    .sort(([a], [b]) => errorsOrder[a]! - errorsOrder[b]!)[0] || ['name'];

  useEffect(() => {
    if (firstErrorKey) {
      flashErrorMessage(errors[firstErrorKey]);
    }
  }, [errors, firstErrorKey, flashErrorMessage]);

  const clearError = useCallback(
    (errorKey: keyof SmartConnector) => {
      if (showMessage) {
        cancelFlash();
      }

      if (firstErrorKey) {
        setErrors((prev) => {
          const { [errorKey]: _, ...rest } = prev;
          return rest;
        });
      }
    },
    [showMessage, cancelFlash, firstErrorKey, setErrors]
  );

  const validation = useMemo(
    () => ({
      name: firstErrorKey === 'name' && { showMessage, message },
      custom_object: firstErrorKey === 'custom_object' && {
        showMessage,
        message,
      },
      connector_type: firstErrorKey === 'connector_type' && {
        showMessage,
        message,
      },
      activity_object: firstErrorKey === 'activity_object' && {
        showMessage,
        message,
      },
    }),
    [showMessage, message, firstErrorKey]
  );

  return {
    errors,
    setErrors,
    validation,
    clearError,
  };
};

type CreateSmartConnectorModalProps = {
  onHide: () => void;
};

export const CreateSmartConnectorModal: React.FC<
  CreateSmartConnectorModalProps
> = ({ onHide }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [showToast] = useToast();

  const [name, setName] = useState('');
  const [selectedObject, setSelectedObject] = useState<GenericOption | null>(
    null
  );
  const [selectedActivity, setSelectedActivity] =
    useState<GenericOption | null>(null);
  const [selectedType, setSelectedType] = useState<SmartConnectorType>(
    SMART_CONNECTOR_TYPES.spreadsheet
  );
  const [submitting, setSubmitting] = useState(false);
  const { errors, setErrors, validation, clearError } = useErrors();

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const { data } = await SmartConnectorService.createSmartConnector({
        name: name.trim(),
        connector_type: selectedType,
        custom_object: selectedObject?.value,
        activity_object: selectedActivity?.value,
        is_diff_check_enabled: true,
      });
      history.push(`${SMARTCONNECTORS_PATHNAME}/${data.id}/settings`);
    } catch (e) {
      const orig = getOriginalError(e);
      if (orig?.name || orig?.custom_object || orig?.connector_type) {
        setErrors({
          name: orig.name || '',
          custom_object: orig.custom_object || '',
          connector_type: orig.connector_type || '',
        });
      } else {
        showToast({
          message:
            getErrorMessage(orig) || t('Failed to create SmartConnector'),
          variant: toastVariant.FAILURE,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangeObject = (value: GenericOption) => {
    setSelectedObject(value);
    clearError('custom_object');
  };

  const handleChangeActivity = (value: GenericOption) => {
    setSelectedActivity(value);
    clearError('activity_object');
  };

  const handleChangeName = (value: string) => {
    setName(value);
    clearError('name');
  };

  const handleChangeType = ({ value }: GenericOption<SmartConnectorType>) => {
    setSelectedType(value);
    clearError('connector_type');
  };

  const { value: customObjects = [], loading: loadingObjects } =
    useAsync(async () => {
      const results = await CustomObjectsService.getCustomObjectListLight();
      return results.map(
        ({ id, objectName }: { id: string; objectName: string }) => ({
          value: id,
          label: objectName,
        })
      );
    }, []);

  const activitiesSelectRef = useRef<HTMLDivElement>(null);

  const isActivityType = selectedType === SMART_CONNECTOR_TYPES.activity;

  const [activitiesSelectProps] = useSelectTypeaheadWithScroll({
    selectRef: activitiesSelectRef,
    entity: Entities.Activities,
    enabled: isActivityType,
    params: {
      enabled: true,
      show_no_objects_associated: true,
    },
  });

  return (
    <BasicModal
      heading={t('Add SmartConnector')}
      onConfirm={handleConfirm}
      onHide={onHide}
      buttonText={t('Save')}
      defaultLeftBtnText={t('Cancel')}
      show
      disabled={
        (isActivityType ? !selectedActivity : !selectedObject) ||
        !name.trim() ||
        submitting ||
        Object.values(errors).some(Boolean)
      }
    >
      <TextInput
        label={t('SmartConnector Name')}
        placeholder={t('Enter Name')}
        value={name}
        onChange={handleChangeName}
        validate={validation['name']}
        error={errors.name}
        {...getDataQAForInput('smart-connector-name', 'text')}
        inModal
        margin
      />
      <Select
        label={t('Choose Connector Type')}
        value={selectedType}
        onChange={handleChangeType}
        options={getSmartConnectorTypeOptions(t)}
        validate={validation['connector_type']}
        error={errors.connector_type}
        {...getDataQAForInput('smart-connector-type', 'dropdown')}
        menuInline
        inModal
        margin
      />
      {isActivityType ? (
        <Select
          ref={activitiesSelectRef}
          label={t('Choose Activity Object')}
          placeholder={t('Choose Activity')}
          value={selectedActivity}
          onChange={handleChangeActivity}
          validate={validation['activity_object']}
          error={errors.activity_object}
          {...activitiesSelectProps}
          {...getDataQAForInput('smart-connector-activity-object', 'dropdown')}
          menuInline
          margin
        />
      ) : null}
      <Select
        label={t('Choose Primary Object')}
        placeholder={t('Choose Object')}
        value={selectedObject}
        onChange={handleChangeObject}
        options={customObjects}
        validate={validation['custom_object']}
        error={errors.custom_object}
        loadItems={loadingObjects}
        {...getDataQAForInput('smart-connector-context-object', 'dropdown')}
        menuInline
      />
    </BasicModal>
  );
};
